import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AgeVerification, isAgeVerified } from '@/components/AgeVerification';
import { storeRoutes } from '@/StoreRoutes';
import { localeFromPath, localizePath, preferredLang } from '@/lib/locale';
import i18n from '@/i18n';
import { InitialDataContext, readInitialData } from '@/lib/initialData';
import { applyStoredConsent } from '@/lib/consent';
import { AdminLogin } from '@/pages/admin/AdminLogin';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminProducts } from '@/pages/admin/AdminProducts';
import { AdminCategories } from '@/pages/admin/AdminCategories';
import { AdminOrders } from '@/pages/admin/AdminOrders';
import { AdminCJImport } from '@/pages/admin/AdminCJImport';
import { AdminSetup } from '@/pages/admin/AdminSetup';
import { toast } from 'sonner';
import '@/App.css';

// Visitors landing on an English (unprefixed) URL who chose — or whose browser
// prefers — Russian or Italian are sent to that version once, on arrival.
const LanguageRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const { pathname, search, hash } = location;
    if (pathname.startsWith('/admin') || localeFromPath(pathname) !== 'en') return;
    const lang = preferredLang();
    if (lang !== 'en') navigate(localizePath(pathname, lang) + search + hash, { replace: true });
    // only on first load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

function App() {
  const [ageVerified, setAgeVerified] = useState(isAgeVerified);
  // Data baked in by the prerender step; read once on startup
  const [initialData] = useState(readInitialData);

  useEffect(() => {
    applyStoredConsent();
  }, []);
  const [cart, setCart] = useState(() => {
    // Initialize from localStorage synchronously to survive page reloads
    try {
      const saved = localStorage.getItem('cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);

  // Persist cart to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
    } catch {
      // storage unavailable (private mode) — cart still works in memory
    }
  }, [cart]);

  // A cart line is identified by product_id + selected_variant (e.g. size)
  const lineKey = (item) => `${item.product_id}::${item.selected_variant || ''}`;

  const handleAddToCart = (product) => {
    const incomingKey = lineKey(product);
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => lineKey(item) === incomingKey);

      if (existingItem) {
        return prevCart.map((item) =>
          lineKey(item) === incomingKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [...prevCart, { ...product, quantity: 1 }];
    });
    // Slide in the mini-cart so the shopper sees what was added
    setCartOpen(true);
  };

  const closeCart = useCallback(() => setCartOpen(false), []);
  const openCart = useCallback(() => setCartOpen(true), []);

  const handleUpdateQuantity = (key, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveItem(key);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        lineKey(item) === key
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const handleRemoveItem = (key) => {
    setCart((prevCart) => prevCart.filter((item) => lineKey(item) !== key));
    toast.success(i18n.t('cart.removed'));
  };

  const handleClearCart = () => {
    setCart([]);
    try {
      localStorage.removeItem('cart');
    } catch {
      // ignore
    }
  };

  const handleVerified = useCallback(() => setAgeVerified(true), []);

  return (
    <div className="App">
      <AgeVerification show={!ageVerified} onVerified={handleVerified} />

      {/* Content is always rendered (and prerendered) under the age gate so
          search engines can read it; it is inert until the visitor confirms. */}
      <div inert={!ageVerified || undefined}>
        <InitialDataContext.Provider value={initialData}>
          <BrowserRouter>
            <LanguageRedirect />
            <Routes>
              {/* Admin Routes - No main navbar/footer */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/setup" element={<AdminSetup />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/cj-import" element={<AdminCJImport />} />
              <Route path="/admin/orders" element={<AdminOrders />} />

              {/* Storefront, once per language (/, /ru/…, /it/…) */}
              {storeRoutes({
                cart,
                cartOpen,
                ageVerified,
                onCartOpen: openCart,
                onCartClose: closeCart,
                onAddToCart: handleAddToCart,
                onUpdateQuantity: handleUpdateQuantity,
                onRemoveItem: handleRemoveItem,
                onClearCart: handleClearCart,
              })}
            </Routes>

            <Toaster position="top-center" />
          </BrowserRouter>
        </InitialDataContext.Provider>
      </div>
    </div>
  );
}

export default App;
